// analytics.sdics.tech — a landing page of cards, each opening its own page,
// with the same pages as tabs in the header: IDs, With IDs not registered,
// Registered voters, Scenario analysis, Sentiment analysis, RRI Initiative
// and Download apps. The National overview dropdown in the header scopes
// every page and is kept while moving between tabs. Signed in with the same
// accounts as RRI USAJILI; every figure is live from /api/analytics/*.

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert, Avatar, Box, Button, CircularProgress, createTheme, CssBaseline, Divider, Grid, ListItemText, Menu, MenuItem, Tab, Tabs,
  ThemeProvider, Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { USAJILIProvider, useUSAJILI } from '@hooks/useUSAJILI';
import { USAJILIColorModeToggle, USAJILIThemeProvider, useUSAJILIColorMode } from '@theme/USAJILIThemeProvider';
import HomeIcon from '@mui/icons-material/Home';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import InsightsIcon from '@mui/icons-material/Insights';
import ForumIcon from '@mui/icons-material/Forum';
import FlagIcon from '@mui/icons-material/Flag';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import dayjs from 'dayjs';
import { HEADER_RED, useNow } from '@components/usajili/USAJILIHeaderStatus';
import { areaName, useDataScope } from './data';
import { IdsSection, NotRegisteredSection, SummaryMapTable, TribeAndPivot } from './IdSections';
import { emptyNrb, normalizeNrb } from './agg';
import { RegisteredSection } from './RegisteredSection';
import { ScenarioSection } from './ScenarioSection';
import { RriSection } from './RriSection';
import { IdLookupSection } from './IdLookupSection';
import { SentimentSection } from './SentimentSection';
import { ReportsSection } from './ReportsSection';
import { C, Dropdown, PALETTES, PaletteMode, setPalette } from './ui';

const BASE = '/analytics';
const LOGIN = '/analytics/login';

interface PageDef {
  path: string;
  label: string;
  icon: React.ReactElement;
  color: string;
  desc: string;
}

const pages = (): PageDef[] => [
  { path: 'ids', label: 'IDs', icon: <BadgeIcon />, color: C.blue, desc: 'ID holders by county, registered and not, with tribe and sex/age pivots and a coverage map.' },
  { path: 'not-registered', label: 'With IDs, not registered', icon: <PersonOffIcon />, color: C.orange, desc: 'ID holders who are not on the voter roll, down to location.' },
  { path: 'registered', label: 'Registered voters', icon: <HowToVoteIcon />, color: C.green, desc: 'Voters by constituency, ward and polling centre, by month, sex, age and tribe.' },
  { path: 'scenario', label: 'Scenario analysis', icon: <InsightsIcon />, color: C.purple, desc: 'What-if projections: registration uptake, turnout and time to a coverage target, by county or tribe.' },
  { path: 'sentiment', label: 'Sentiment analysis', icon: <ForumIcon />, color: C.cyan, desc: 'Registration sentiment: which counties are gaining or losing registration pace.' },
  { path: 'rri', label: 'RRI Initiative', icon: <FlagIcon />, color: C.yellow, desc: 'The Rapid Results Initiative counties: RRI returns beside the voter roll.' },
  { path: 'lookup', label: 'ID lookup', icon: <PersonSearchIcon />, color: C.cyan, desc: 'Check whether IDs are registered: one or up to 500 at once.' },
  { path: 'reports', label: 'Reports', icon: <AssessmentIcon />, color: C.blue, desc: 'Download the people behind the figures as a PDF: unregistered or registered, by area down to village, with clan, family name and phone.' },
];

/** Its own theme per mode, so the main app's theme doesn't leak in. */
const themeFor = (mode: PaletteMode) => {
  const p = PALETTES[mode];
  return createTheme({
    palette: {
      mode,
      background: { default: p.bg, paper: p.card },
      text: { primary: p.text, secondary: p.muted },
      primary: { main: p.green },
      divider: p.border,
    },
    typography: { fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif' },
    components: { MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } } },
  });
};
const THEMES = { dark: themeFor('dark'), light: themeFor('light') };

/** Light or dark, shared with the login and RRI pages (the toggle in the
 *  header). The pages remount on a switch so they re-read the colours. */
const Themed: React.FC = () => {
  const { mode } = useUSAJILIColorMode();
  setPalette(mode);
  return (
    <ThemeProvider theme={THEMES[mode]}>
      <CssBaseline />
      <USAJILIProvider>
        <AuthGate key={mode} />
      </USAJILIProvider>
    </ThemeProvider>
  );
};

export const AnalyticsPage: React.FC = () => (
  <USAJILIThemeProvider>
    <Themed />
  </USAJILIThemeProvider>
);

/** The same accounts as RRI USAJILI (ADMIN or HQ); others go to the login. */
const AuthGate: React.FC = () => {
  const { loading, role, error } = useUSAJILI();
  const navigate = useNavigate();
  const hasToken = !!localStorage.getItem('sdics_access_token');
  useEffect(() => {
    if (!loading && !role && !hasToken) navigate(LOGIN, { replace: true });
  }, [loading, role, hasToken, navigate]);
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', backgroundColor: C.bg }}>
        <CircularProgress sx={{ color: C.green }} />
      </Box>
    );
  }
  if (!role) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: C.bg, p: 6 }}>
        <Alert severity="warning" sx={{ maxWidth: 640, mx: 'auto' }}
          action={<Button color="inherit" size="small" onClick={() => navigate(LOGIN)}>Sign in</Button>}>
          {error || 'Please sign in.'}
        </Alert>
      </Box>
    );
  }
  return <AnalyticsShell />;
};

const UserMenu: React.FC = () => {
  const { officer, role, logout } = useUSAJILI();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const initials = (officer?.full_name || 'U').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <>
      <Avatar onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ cursor: 'pointer', width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700, bgcolor: C.orange, mb: 0.5 }}>
        {initials}
      </Avatar>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { backgroundColor: C.card, color: C.text, border: `1px solid ${C.border}` } }}>
        <MenuItem disabled>
          <ListItemText primary={officer?.full_name} secondary={role}
            primaryTypographyProps={{ fontSize: '0.85rem' }} secondaryTypographyProps={{ fontSize: '0.75rem', color: C.muted }} />
        </MenuItem>
        <Divider sx={{ borderColor: C.border }} />
        <MenuItem onClick={async () => { setAnchor(null); await logout(); navigate(LOGIN, { replace: true }); }}>
          <LogoutIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
          <Typography variant="body2">Sign out</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

/** Red line under the brand, as on the RRI dashboard: the RRI page shows the
 *  RRI returns' date range (13 counties); every other page says the voter-roll
 *  registrations are synced to date (all counties). Then today's date and time. */
const SyncStatus: React.FC<{ rri: boolean }> = ({ rri }) => {
  const { mode } = useUSAJILIColorMode();
  const { metadata } = useUSAJILI();
  const now = useNow();
  const from = metadata?.data_from;
  const to = metadata?.data_to;
  const parts = rri
    ? [
        'RRI returns',
        from && to ? `${dayjs(from).format('D MMM')} – ${dayjs(to).format('D MMM YYYY')}` : null,
        metadata?.counties?.length ? `${metadata.counties.length} counties` : null,
      ]
    : ['Data synced to date', 'registrations updated · all counties'];
  return (
    <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.9rem', md: '0.98rem' }, fontWeight: 700, color: HEADER_RED[mode], lineHeight: 1.3, mt: 0.25 }}>
      {[...parts.filter(Boolean), dayjs(now).format('ddd D MMM YYYY, HH:mm')].join(' · ')}
    </Typography>
  );
};

const AnalyticsShell: React.FC = () => {
  const [county, setCounty] = useState('');
  const scope = useDataScope();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const current = pathname.replace(/\/+$/, '').slice(BASE.length).replace(/^\//, '').split('/')[0];
  const page = pages().find((p) => p.path === current);
  const open = (path: string) => { navigate(path ? `${BASE}/${path}` : BASE); window.scrollTo({ top: 0 }); };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: C.bg, color: C.text }}>
      {/* Header: brand, National overview, tabs */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: `${C.bg}f2`, backdropFilter: 'blur(6px)', borderBottom: `1px solid ${C.border}` }}>
        <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 3 }, pt: 1.25, display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
          <Box onClick={() => open('')} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mr: 'auto', pb: 0.5, cursor: 'pointer' }}>
            <img src="/coat-of-arms-sm.png" alt="" style={{ height: 34 }} />
            <Box>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800, lineHeight: 1.1 }}>SDICS Analytics</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: C.muted }}>
                IDs, voter registration and projections · registrations to date
              </Typography>
              <SyncStatus rri={page?.path === 'rri'} />
            </Box>
          </Box>
          <Dropdown label="National overview" value={county} options={scope.counties.map((c) => ({ value: c, label: areaName(c) }))}
            onChange={setCounty} placeholder="Kenya (all counties)" minWidth={220} hint={false} />
          <Box sx={{ mb: 0.5 }}><USAJILIColorModeToggle color={C.muted} /></Box>
          <UserMenu />
        </Box>
        <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 1, md: 2 } }}>
          <Tabs value={page ? page.path : 'home'} onChange={(_, v) => open(v === 'home' ? '' : v)} variant="scrollable" scrollButtons="auto"
            TabIndicatorProps={{ style: { backgroundColor: page?.color || C.green, height: 3 } }}
            sx={{ minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontSize: '0.8rem', fontWeight: 600, color: C.muted, px: 1.75 } }}>
            <Tab value="home" icon={<HomeIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Home" sx={{ '&.Mui-selected': { color: C.text } }} />
            {pages().map((p) => (
              <Tab key={p.path} value={p.path} icon={React.cloneElement(p.icon, { sx: { fontSize: 18 } })} iconPosition="start" label={p.label}
                sx={{ '&.Mui-selected': { color: p.color } }} />
            ))}
          </Tabs>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
        {scope.error && <Alert severity="error" sx={{ mb: 2 }}>{(scope.error as any)?.message || 'Could not load the data.'}</Alert>}

        {!page && <Landing county={county} onCounty={setCounty} onOpen={open} />}
        {page?.path === 'ids' && <IdsSection county={county} />}
        {page?.path === 'not-registered' && <NotRegisteredSection county={county} />}
        {page?.path === 'registered' && <RegisteredSection county={county} />}
        {page?.path === 'scenario' && <ScenarioSection county={county} />}
        {page?.path === 'sentiment' && <SentimentSection county={county} />}
        {page?.path === 'rri' && <RriSection county={county} onCounty={setCounty} />}
        {page?.path === 'lookup' && <IdLookupSection />}
        {page?.path === 'reports' && <ReportsSection county={county} />}

        <Typography sx={{ fontSize: '0.68rem', color: C.muted, textAlign: 'center', mt: 4 }}>
          SDICS Analytics · live data · IEBC voter roll and NRB records
        </Typography>
      </Box>
    </Box>
  );
};

/** Home: one card per page (each opens that page), then the map and summary table. */
const Landing: React.FC<{ county: string; onCounty: (c: string) => void; onOpen: (path: string) => void }> = ({ county, onCounty, onOpen }) => (
  <Box>
    <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, mb: 0.5 }}>Welcome</Typography>
    <Typography sx={{ fontSize: '0.82rem', color: C.muted, mb: 3 }}>
      Choose an area to explore{county ? `, focused on ${areaName(county)}` : ''}. Use National overview at the top to focus every page on one county.
    </Typography>
    <Grid container spacing={2}>
      {pages().map((p) => (
        <Grid item xs={12} sm={6} md={4} key={p.path}>
          <Box onClick={() => onOpen(p.path)} sx={{
            p: 2.5, height: '100%', minHeight: 150, borderRadius: 1.5, cursor: 'pointer', display: 'flex', flexDirection: 'column',
            background: `linear-gradient(135deg, ${p.color}26 0%, ${C.card} 65%)`, border: `1px solid ${C.border}`,
            transition: 'transform .15s, border-color .15s',
            '&:hover': { transform: 'translateY(-2px)', borderColor: p.color },
            '&:hover .go': { color: p.color, transform: 'translateX(3px)' },
          }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 1.25, backgroundColor: `${p.color}26`, color: p.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
            }}>{React.cloneElement(p.icon, { sx: { fontSize: 26 } })}</Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>{p.label}</Typography>
            <Typography sx={{ fontSize: '0.76rem', color: C.muted, mt: 0.5, flex: 1 }}>{p.desc}</Typography>
            <Box className="go" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, fontSize: '0.75rem', fontWeight: 600, color: C.muted, transition: 'all .15s' }}>
              Open <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>

    <Typography sx={{ fontSize: '0.7rem', color: C.muted, fontWeight: 700, letterSpacing: 0.6, mt: 4, mb: 1 }}>
      {(areaName(county) || 'Kenya').toUpperCase()} · SUMMARY
    </Typography>
    <SummaryMapTable county={county} onCounty={onCounty} />

    <Typography sx={{ fontSize: '0.7rem', color: C.muted, fontWeight: 700, letterSpacing: 0.6, mt: 4, mb: 1 }}>
      {(areaName(county) || 'Kenya').toUpperCase()} · PIVOTS AND SUMMARY BY TRIBE
    </Typography>
    <TribeAndPivot f={normalizeNrb({ ...emptyNrb, county })} scope={areaName(county) || 'Kenya'}
      level={county ? (county === 'PRISONS' ? 'location' : 'subcounty') : 'county'}
      onDrill={county ? undefined : onCounty} measure0="ids" />
  </Box>
);

export default AnalyticsPage;
